/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useEffect, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Link, TextInput } from '@carbon/react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { objectToArray } from '../utils/common';
import CredentialsSelector from './CredentialsSelector';

function TestToolIdentification({ flags = {}, isEnforced, onChange = () => {}, options = {}, externalApiKeys, apps = [], onCopyClientIdCallback = () => {} }) {
  const [clientID, setClientID] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [isClientIdCustom, setIsClientIdCustom] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // initialise with default identity if we don't have one.
    if (clientID === null && isEnforced && get(apps, '[0].credentials[0].client_id')) {
      setClientID(apps[0].credentials[0].client_id);

      const initialRedirectUri = apps[0].redirectUri;
      let initialRedirectUris = [''];
      if (typeof initialRedirectUri === 'string') {
        initialRedirectUris[0] = initialRedirectUri;
      } else if (initialRedirectUri && initialRedirectUri.length > 0) {
        initialRedirectUris = initialRedirectUri;
      }
    }

    if (flags && flags.externalApiKeys && Object.keys(flags.externalApiKeys).length > 0 && Object.keys(externalApiKeys).length === 0) {
      const initialExternalApiKeys = externalApiKeys;
      objectToArray(flags.externalApiKeys).forEach((apiKey) => {
        initialExternalApiKeys[apiKey.key] = apiKey.value;
        initialExternalApiKeys[apiKey.key].value = '';
      });
    }
  }, [apps, clientID, externalApiKeys, flags, isEnforced]);

  const getAppByClientId = (clientID) => {
    let match = null;
    apps.forEach((app) => {
      app.credentials.forEach((cred) => {
        if (cred.client_id === clientID) {
          match = app;
        }
      });
    });
    return match;
  };
  const handleClientIdChange = ({ selectedCredential, isCustomCredential }) => {
    let value = selectedCredential;
    const app = getAppByClientId(value);
    if (app) {
      const initialRedirectUri = app.redirectUri;
      let redirectUris = [''];
      if (typeof initialRedirectUri === 'string') {
        redirectUris[0] = initialRedirectUri;
      } else if (initialRedirectUri && initialRedirectUri.length > 0) {
        redirectUris = initialRedirectUri;
      }
      const redirectUri = redirectUris[0];

      setClientID(value);

      onChange({ clientID: value, redirectUri, redirectUris });
    } else {
      if (!value) {
        value = '';
      }
      setClientID(value);
      onChange({ clientID: value, redirectUri: '', redirectUris: [] });
    }
    setIsClientIdCustom(!!isCustomCredential);
  };

  const handleClientSecretChange = (event) => {
    setClientSecret(event.target.value);
    if (onChange && typeof onChange === 'function') {
      onChange({ clientSecret: event.target.value });
    }
  };

  const handleExternalApiKeyChange = (key, event) => {
    const newExternalApiKeys = Object.assign(externalApiKeys);
    newExternalApiKeys[key].value = event.target.value;
    if (onChange && typeof onChange === 'function') {
      onChange({ externalApiKeys: newExternalApiKeys });
    }
  };

  const mustLogIn = options && options.loginLink && !options.loggedIn;
  const haveApps = apps && apps.length > 0;
  const portalAppLink = options && options.isApicPortal && options.apicPortalUserid !== 1 && options.apicPortalNewAppLink;
  return (
    <Accordion className="testToolIdentification" align="start">
      <AccordionItem className="testToolIdentificationInput" title={t('identification')} open>
        {/* eslint-disable-next-line no-nested-ternary */}
        {mustLogIn ? (
          <Link className="loginLink" href={options.loginLink}>
            {t('login')}
          </Link>
        ) : // eslint-disable-next-line no-nested-ternary
        isEnforced ? (
          !haveApps && portalAppLink ? (
            <Link className="appLink" href={options.apicPortalNewAppLink}>
              {t('register_app')}
            </Link>
          ) : (
            <div className="clientCreds">
              {flags && flags.requiresClientId && !(options.fixedCredentials && options.fixedCredentials.clientId) && (
                <div className="clientID">
                  <div className="clientCredsHeader">
                    <div className="labelText">{t('client_id')}</div>
                    {!isClientIdCustom && (
                      <button
                        data-testid="copy-client-id-button"
                        className="copyClientIdButton"
                        type="button"
                        tabIndex="0"
                        onClick={onCopyClientIdCallback}
                      >
                        {t('copy_client_id')}
                      </button>
                    )}
                  </div>
                  <CredentialsSelector
                    allowCustom
                    onChange={handleClientIdChange}
                    className="clientIdInput"
                    onSelectorChange={() => {
                      handleClientSecretChange({ target: { value: '' } });
                    }}
                  />
                  <div className="subText">{t('subscribed_creds_explanation')}</div>
                </div>
              )}

              {flags && flags.requiresClientSecret && !(options.fixedCredentials && options.fixedCredentials.clientSecret) && (
                <div className="clientSecret">
                  <div className="labelText">{t('client_secret')}</div>
                  <TextInput
                    required
                    labelText={t('client_secret')}
                    hideLabel
                    data-testid="client_secret"
                    value={clientSecret ?? ''}
                    id="clientSecret"
                    name="clientSecret"
                    type="password"
                    onChange={handleClientSecretChange}
                  />
                </div>
              )}
            </div>
          )
        ) : !haveApps ? (
          <div>{t('request_security_credentials')}</div>
        ) : (
          <div className="clientCreds">
            {flags && flags.requiresClientId && !(options.fixedCredentials && options.fixedCredentials.clientId) && (
              <div className="clientID">
                <div className="labelText">{t('client_id')}</div>
                <TextInput
                  data-testid="clientID"
                  required
                  id="client_id"
                  name="clientId"
                  value={clientID ?? ''}
                  labelText={t('client_id')}
                  hideLabel
                  onChange={(e) => {
                    handleClientIdChange({ selectedCredential: get(e, 'target.value') });
                  }}
                />
              </div>
            )}

            {flags && flags.requiresClientSecret && !(options.fixedCredentials && options.fixedCredentials.clientSecret) && (
              <div className="clientSecret">
                <div className="labelText">{t('client_secret')}</div>
                <TextInput
                  data-testid="client_secret"
                  required
                  labelText={t('client_secret')}
                  hideLabel
                  value={clientSecret ?? ''}
                  id="clientSecret"
                  name="clientSecret"
                  type="password"
                  onChange={handleClientSecretChange}
                />
              </div>
            )}
          </div>
        )}
        {flags &&
          flags.externalApiKeys &&
          Object.keys(flags.externalApiKeys).length > 0 &&
          objectToArray(flags.externalApiKeys).map((apiKey) => {
            const apiKeyValue = externalApiKeys[apiKey.key] ? externalApiKeys[apiKey.key].value : '';
            return (
              <div className="externalApiKeyField" key={apiKey.key}>
                <TextInput
                  id={apiKey.key}
                  name={apiKey.key}
                  type="input"
                  labelText={t('api key')}
                  onChange={(e) => handleExternalApiKeyChange(apiKey.key, e)}
                  value={apiKeyValue ?? ''}
                />
              </div>
            );
          })}
      </AccordionItem>
    </Accordion>
  );
}

TestToolIdentification.propTypes = {
  onChange: PropTypes.func,
  apps: PropTypes.array,
  flags: PropTypes.object,
  options: PropTypes.object,
  onCopyClientIdCallback: PropTypes.func
};

export default withTranslation()(TestToolIdentification);
